/**
 * Audit Service (QMS) — Lifecycle Management
 * PRD: docs/PRD_Audit_Lifecycle_v2.md Section 6.1
 * 
 * Manages audits collection with full lifecycle: planificat → in_desfasurare → constatari → inchis
 * 
 * IMPORTANT: This is auditService_qms.js (QMS audits with lifecycle).
 * Do NOT confuse with auditService.js (localStorage UI audit trail).
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from '../firebase';
import auditService from './auditService';

const COLLECTION = 'audits';

export const AUDIT_QMS_STATUSES = ['planificat', 'in_desfasurare', 'constatari', 'inchis', 'redeschis'];
export const AUDIT_QMS_RESULTS = ['conform', 'neconform', 'conform_cu_recomandari'];
export const AUDIT_TYPES = ['internal', 'anmcs', 'external'];

function omitUndefined(obj) {
  if (obj == null || typeof obj !== 'object') return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

export class AuditServiceQMS {
  constructor() {
    this.collectionRef = collection(db, COLLECTION);
  }

  /**
   * Create new audit
   * @param {string} organizationId
   * @param {object} data - audit fields
   * @param {string} userId - creator
   * @returns {Promise<string>} - audit ID
   */
  async create(organizationId, data, userId) {
    if (!organizationId) throw new Error('organizationId is required');
    if (!data.title || data.title.trim().length < 5) {
      throw new Error('Title is required (min 5 characters)');
    }
    if (!data.plannedDate) throw new Error('plannedDate is required');
    if (!data.auditorId) throw new Error('auditorId is required');
    if (!data.organizationalScope?.nodeId) throw new Error('organizationalScope.nodeId is required');
    if (!data.auditType || !AUDIT_TYPES.includes(data.auditType)) {
      throw new Error(`auditType must be one of: ${AUDIT_TYPES.join(', ')}`);
    }

    const now = Timestamp.now();
    const payload = {
      organizationId,
      
      // Identification
      auditType: data.auditType,
      title: data.title.trim(),
      objective: data.objective?.trim() || '',
      auditThemeIds: data.auditThemeIds || [],
      auditThemeLabels: data.auditThemeLabels || [],
      headNurseId: data.headNurseId || null,
      headNurseName: data.headNurseName || '',
      observatiiGenerale: data.observatiiGenerale || [],
      puncteForte: data.puncteForte || [],
      puncteImbunatatit: data.puncteImbunatatit || [],
      oportunitatiImbunatatire: data.oportunitatiImbunatatire || [],
      riscuriIdentificate: data.riscuriIdentificate || [],
      rating: data.rating ?? null,
      photos: data.photos || [],
      notificationRecipients: data.notificationRecipients || { to: [], cc: [] },
      
      // Organizational scope
      organizationalScope: {
        nodeId: data.organizationalScope.nodeId,
        nodeLabel: data.organizationalScope.nodeLabel || '',
        nodeType: data.organizationalScope.nodeType || 'hospital'
      },
      
      // Planning
      plannedDate: data.plannedDate instanceof Timestamp ? data.plannedDate : Timestamp.fromDate(new Date(data.plannedDate)),
      auditorId: data.auditorId,
      auditorName: data.auditorName || '',
      
      // Witnesses
      witnesses: data.witnesses || [],
      
      // Assessment instances
      assessmentInstances: data.assessmentInstances || [],
      
      // Content
      findings: data.findings?.trim() || '',
      conclusions: data.conclusions?.trim() || '',
      recommendations: data.recommendations?.trim() || '',
      
      // Actions
      actions: data.actions || [],
      
      // Result
      result: data.result || null,
      
      // Next audit
      nextAuditDate: data.nextAuditDate ? (data.nextAuditDate instanceof Timestamp ? data.nextAuditDate : Timestamp.fromDate(new Date(data.nextAuditDate))) : null,
      nextAuditNotes: data.nextAuditNotes?.trim() || null,
      
      // Lifecycle
      status: 'planificat',
      reopenNote: null,
      
      // Metadata
      createdAt: now,
      createdBy: userId,
      updatedAt: now,
      updatedBy: userId,
      closedAt: null
    };

    const docRef = await addDoc(this.collectionRef, payload);

    try {
      auditService.logCreate('audits', docRef.id, data.title.slice(0, 50), {
        auditType: data.auditType,
        status: 'planificat'
      });
    } catch (e) {
      console.warn('[AuditServiceQMS] Audit log failed:', e);
    }

    return docRef.id;
  }

  /**
   * Update audit fields
   * @param {string} auditId
   * @param {object} updates
   * @param {string} userId
   */
  async update(auditId, updates, userId) {
    if (!auditId) throw new Error('auditId is required');
    
    const ref = doc(this.collectionRef, auditId);
    const current = await getDoc(ref);
    if (!current.exists()) throw new Error('Audit not found');
    
    const currentData = current.data();
    
    // Validate status-specific edits
    if (currentData.status === 'inchis' && !updates.status) {
      throw new Error('Cannot edit closed audit. Reopen first.');
    }

    const patch = omitUndefined({
      ...updates,
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });

    // Convert dates
    if (updates.plannedDate && !(updates.plannedDate instanceof Timestamp)) {
      patch.plannedDate = Timestamp.fromDate(new Date(updates.plannedDate));
    }
    if (updates.nextAuditDate && !(updates.nextAuditDate instanceof Timestamp)) {
      patch.nextAuditDate = Timestamp.fromDate(new Date(updates.nextAuditDate));
    }

    await updateDoc(ref, patch);

    try {
      auditService.logUpdate('audits', auditId, currentData.title?.slice(0, 50) || auditId, {
        changedFields: Object.keys(updates)
      });
    } catch (e) {
      console.warn('[AuditServiceQMS] Audit log failed:', e);
    }
  }

  /**
   * Transition audit status with validation
   * @param {string} auditId
   * @param {string} newStatus
   * @param {object} extraData - additional data for transition (e.g. reopenNote)
   * @param {string} userId
   */
  async transitionStatus(auditId, newStatus, extraData = {}, userId) {
    if (!AUDIT_QMS_STATUSES.includes(newStatus)) {
      throw new Error(`Invalid status: ${newStatus}`);
    }

    const ref = doc(this.collectionRef, auditId);
    const current = await getDoc(ref);
    if (!current.exists()) throw new Error('Audit not found');
    
    const currentData = current.data();
    const currentStatus = currentData.status;

    // Validate transitions
    const validTransitions = {
      'planificat': ['in_desfasurare'],
      'in_desfasurare': ['constatari'],
      'constatari': ['inchis'],
      'inchis': ['redeschis'],
      'redeschis': ['constatari', 'inchis']
    };

    if (!validTransitions[currentStatus]?.includes(newStatus)) {
      throw new Error(`Invalid transition: ${currentStatus} → ${newStatus}`);
    }

    // Validate transition-specific requirements
    if (newStatus === 'in_desfasurare') {
      if (!currentData.assessmentInstances || currentData.assessmentInstances.length === 0) {
        throw new Error('Cannot start audit: at least one assessment instrument is required');
      }
      const allHaveThemes = currentData.assessmentInstances.every(
        (inst) => inst.selectedThemeIds && inst.selectedThemeIds.length > 0
      );
      if (!allHaveThemes) {
        throw new Error('Cannot start audit: all instruments must have at least one theme selected');
      }
    }

    if (newStatus === 'constatari') {
      const allCompleted = currentData.assessmentInstances.every(
        (inst) => inst.status === 'completed'
      );
      if (!allCompleted) {
        throw new Error('Cannot move to findings: all assessment instances must be completed');
      }
    }

    if (newStatus === 'inchis') {
      if (!currentData.result) {
        throw new Error('Cannot close audit: result is required');
      }
      if (!currentData.conclusions || currentData.conclusions.trim().length < 20) {
        throw new Error('Cannot close audit: conclusions must be at least 20 characters');
      }
    }

    if (newStatus === 'redeschis') {
      if (!extraData.reopenNote || extraData.reopenNote.trim().length < 10) {
        throw new Error('Cannot reopen audit: reopenNote is required (min 10 characters)');
      }
    }

    // Build update
    const patch = {
      status: newStatus,
      updatedAt: Timestamp.now(),
      updatedBy: userId
    };

    if (newStatus === 'inchis') {
      patch.closedAt = Timestamp.now();
    }

    if (newStatus === 'redeschis') {
      patch.reopenNote = extraData.reopenNote.trim();
      patch.closedAt = null;
    }

    await updateDoc(ref, patch);

    if (newStatus === 'inchis') {
      const auditDataForDownstream = { ...currentData, ...patch, id: auditId };
      setTimeout(() => {
        this._generateDownstreamOnClose(auditId, auditDataForDownstream, userId).catch((e) =>
          console.warn('[AuditServiceQMS] _generateDownstreamOnClose failed (non-blocking):', e)
        );
      }, 0);
    }

    try {
      auditService.logUpdate('audits', auditId, currentData.title?.slice(0, 50) || auditId, {
        statusTransition: `${currentStatus} → ${newStatus}`
      });
    } catch (e) {
      console.warn('[AuditServiceQMS] Audit log failed:', e);
    }
  }

  /**
   * Generează CAPA și recommendations la închiderea auditului.
   * Apelat non-blocking din transitionStatus() la newStatus === 'inchis'.
   */
  async _generateDownstreamOnClose(auditId, auditData, userId) {
    const actions = auditData.actions || [];
    const updatedActions = actions.map((a) => ({ ...a }));
    let hasChanges = false;

    for (let i = 0; i < actions.length; i++) {
      const action = actions[i];

      if (['corectie', 'capa'].includes(action.type) && !action.capaId) {
        try {
          const { capaService } = await import('./capaService');
          const capaId = await capaService.create(
            {
              organizationId: auditData.organizationId,
              sourceType: 'audit',
              auditFindingId: action.id,
              sourceRef: `${auditData.title?.slice(0, 50) || ''} - ${(action.description || '').slice(0, 30)}`,
              rcaId: null,
              riskId: null,
              deviationId: null,
              withoutRca: true,
              rcaReason: 'Acțiune corectivă identificată direct în audit — cauza documentată de auditor',
              actions: [
                {
                  actionType: 'corrective',
                  description: action.description || '',
                  ownerId: action.assignedTo || null,
                  ownerName: action.assignedToName || '',
                  ownerGroupId: action.assignedToGroup || null,
                  ownerGroupName: action.assignedToGroupName || '',
                  dueDate: action.dueDate || null,
                  protocolRef: null
                }
              ],
              effectivenessVerification: ''
            },
            userId
          );
          updatedActions[i] = { ...updatedActions[i], capaId };
          hasChanges = true;
        } catch (e) {
          console.warn(`[AuditServiceQMS] CAPA generation failed for action ${action.id}:`, e?.message || e);
        }
      }

      if (action.type === 'recomandare' && !action.recommendationId) {
        try {
          const { recommendationService } = await import('./recommendationService');
          const recId = await recommendationService.createFromAuditAction(auditId, auditData, action, userId);
          updatedActions[i] = { ...updatedActions[i], recommendationId: recId };
          hasChanges = true;
        } catch (e) {
          console.warn(`[AuditServiceQMS] Recommendation generation failed for action ${action.id}:`, e?.message || e);
        }
      }
    }

    if (hasChanges) {
      try {
        const docRef = doc(this.collectionRef, auditId);
        await updateDoc(docRef, {
          actions: updatedActions,
          updatedAt: Timestamp.now(),
          updatedBy: userId
        });
      } catch (e) {
        console.warn('[AuditServiceQMS] Failed to update actions with generated IDs:', e);
      }
    }
  }

  /**
   * Get audit by ID
   * @param {string} auditId
   * @returns {Promise<object|null>}
   */
  async getById(auditId) {
    if (!auditId) return null;
    const ref = doc(this.collectionRef, auditId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    
    const data = snap.data();
    return {
      id: snap.id,
      ...data,
      plannedDate: data.plannedDate?.toDate?.() ?? data.plannedDate,
      nextAuditDate: data.nextAuditDate?.toDate?.() ?? data.nextAuditDate,
      createdAt: data.createdAt?.toDate?.() ?? data.createdAt,
      updatedAt: data.updatedAt?.toDate?.() ?? data.updatedAt,
      closedAt: data.closedAt?.toDate?.() ?? data.closedAt
    };
  }

  /**
   * Get audits by organization with filters
   * @param {string} organizationId
   * @param {object} filters - { status, auditType, dateFrom, dateTo }
   * @returns {Promise<array>}
   */
  async getByOrganization(organizationId, filters = {}) {
    if (!organizationId) return [];
    
    const constraints = [where('organizationId', '==', organizationId)];
    
    if (filters.status) {
      constraints.push(where('status', '==', filters.status));
    }
    if (filters.auditType) {
      constraints.push(where('auditType', '==', filters.auditType));
    }
    
    constraints.push(orderBy('plannedDate', 'desc'));
    
    const q = query(this.collectionRef, ...constraints);
    const snapshot = await getDocs(q);
    
    let results = snapshot.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        ...data,
        plannedDate: data.plannedDate?.toDate?.() ?? data.plannedDate,
        nextAuditDate: data.nextAuditDate?.toDate?.() ?? data.nextAuditDate,
        createdAt: data.createdAt?.toDate?.() ?? data.createdAt,
        updatedAt: data.updatedAt?.toDate?.() ?? data.updatedAt,
        closedAt: data.closedAt?.toDate?.() ?? data.closedAt
      };
    });

    // Client-side date filtering if needed
    if (filters.dateFrom) {
      const from = new Date(filters.dateFrom);
      results = results.filter((r) => new Date(r.plannedDate) >= from);
    }
    if (filters.dateTo) {
      const to = new Date(filters.dateTo);
      results = results.filter((r) => new Date(r.plannedDate) <= to);
    }

    return results;
  }

  /**
   * Delete audit (only if status === 'planificat')
   * @param {string} auditId
   * @param {string} userId
   */
  async delete(auditId, userId) {
    if (!auditId) throw new Error('auditId is required');
    
    const ref = doc(this.collectionRef, auditId);
    const current = await getDoc(ref);
    if (!current.exists()) throw new Error('Audit not found');
    
    const data = current.data();
    if (data.status !== 'planificat') {
      throw new Error('Can only delete audits in planificat status');
    }

    await deleteDoc(ref);

    try {
      auditService.logDelete('audits', auditId, data.title?.slice(0, 50) || auditId, {
        status: data.status
      });
    } catch (e) {
      console.warn('[AuditServiceQMS] Audit log failed:', e);
    }
  }

  /**
   * Add assessment instance to audit
   * @param {string} auditId
   * @param {object} instance - { instanceId, instrumentId, instrumentName, selectedThemeIds, selectedThemeLabels }
   * @param {string} userId
   */
  async addAssessmentInstance(auditId, instance, userId) {
    if (!auditId) throw new Error('auditId is required');
    if (!instance.instanceId) throw new Error('instance.instanceId is required');
    if (!instance.instrumentId) throw new Error('instance.instrumentId is required');
    if (!instance.selectedThemeIds || instance.selectedThemeIds.length === 0) {
      throw new Error('instance.selectedThemeIds is required (min 1)');
    }

    const ref = doc(this.collectionRef, auditId);
    const current = await getDoc(ref);
    if (!current.exists()) throw new Error('Audit not found');

    const data = current.data();
    if (data.status === 'inchis') {
      throw new Error('Cannot add instruments to closed audit');
    }

    const newInstance = {
      instanceId: instance.instanceId,
      instrumentId: instance.instrumentId,
      instrumentName: instance.instrumentName || '',
      selectedThemeIds: instance.selectedThemeIds,
      selectedThemeLabels: instance.selectedThemeLabels || [],
      status: 'pending',
      completedAt: null,
      resultSummary: null
    };

    const updates = {
      assessmentInstances: arrayUnion(newInstance),
      updatedAt: Timestamp.now(),
      updatedBy: userId
    };
    // New pending instance: audit must leave “findings-only” phase until all are completed again
    if (data.status === 'constatari' || data.status === 'redeschis') {
      updates.status = 'in_desfasurare';
    }

    await updateDoc(ref, updates);
  }

  /**
   * Remove assessment instance from audit
   * @param {string} auditId
   * @param {string} instanceId
   * @param {string} userId
   */
  async removeAssessmentInstance(auditId, instanceId, userId) {
    if (!auditId) throw new Error('auditId is required');
    if (!instanceId) throw new Error('instanceId is required');

    const ref = doc(this.collectionRef, auditId);
    const current = await getDoc(ref);
    if (!current.exists()) throw new Error('Audit not found');

    const data = current.data();
    if (data.status === 'inchis') {
      throw new Error('Cannot remove instruments from closed audit');
    }

    const instance = data.assessmentInstances?.find((i) => i.instanceId === instanceId);
    if (!instance) throw new Error('Assessment instance not found');

    await updateDoc(ref, {
      assessmentInstances: arrayRemove(instance),
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });

    const fresh = await getDoc(ref);
    const d = fresh.data();
    const instances = d.assessmentInstances || [];
    const allCompleted =
      instances.length > 0 && instances.every((i) => i.status === 'completed');
    if (
      (d.status === 'constatari' || d.status === 'redeschis') &&
      !allCompleted
    ) {
      await updateDoc(ref, {
        status: 'in_desfasurare',
        updatedAt: Timestamp.now(),
        updatedBy: userId
      });
    }
  }

  /**
   * Update assessment instance in audit (called by assessmentInstanceService on completion)
   * @param {string} auditId
   * @param {string} instanceId
   * @param {object} updates - { status, completedAt, resultSummary }
   */
  async updateAssessmentInstance(auditId, instanceId, updates) {
    if (!auditId) throw new Error('auditId is required');
    if (!instanceId) throw new Error('instanceId is required');

    const ref = doc(this.collectionRef, auditId);
    const current = await getDoc(ref);
    if (!current.exists()) throw new Error('Audit not found');

    const data = current.data();
    const instances = data.assessmentInstances || [];
    const index = instances.findIndex((i) => i.instanceId === instanceId);
    
    if (index === -1) throw new Error('Assessment instance not found in audit');

    // Update the instance
    instances[index] = {
      ...instances[index],
      ...updates
    };

    await updateDoc(ref, {
      assessmentInstances: instances,
      updatedAt: Timestamp.now()
    });

    // Check if all instances are completed → auto-transition to constatari
    if (data.status === 'in_desfasurare') {
      const allCompleted = instances.every((inst) => inst.status === 'completed');
      if (allCompleted) {
        await updateDoc(ref, {
          status: 'constatari',
          updatedAt: Timestamp.now()
        });
        console.log(`[AuditServiceQMS] Auto-transitioned audit ${auditId} to constatari`);
      }
    }
  }

  /**
   * Add witness to audit
   * @param {string} auditId
   * @param {object} witness - { id, type, userId, name, role }
   * @param {string} userId
   */
  async addWitness(auditId, witness, userId) {
    if (!auditId) throw new Error('auditId is required');
    if (!witness.type || !['system_user', 'external'].includes(witness.type)) {
      throw new Error('witness.type must be system_user or external');
    }
    if (!witness.name) throw new Error('witness.name is required');

    const ref = doc(this.collectionRef, auditId);
    const current = await getDoc(ref);
    if (!current.exists()) throw new Error('Audit not found');

    const data = current.data();
    if (data.status === 'inchis') {
      throw new Error('Cannot add witnesses to closed audit');
    }

    const newWitness = {
      id: witness.id || `witness_${Date.now()}`,
      type: witness.type,
      userId: witness.userId || null,
      name: witness.name,
      role: witness.role || '',
      phoneNumber: witness.phoneNumber || null
    };

    await updateDoc(ref, {
      witnesses: arrayUnion(newWitness),
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
  }

  /**
   * Remove witness from audit
   * @param {string} auditId
   * @param {string} witnessId
   * @param {string} userId
   */
  async removeWitness(auditId, witnessId, userId) {
    if (!auditId) throw new Error('auditId is required');
    if (!witnessId) throw new Error('witnessId is required');

    const ref = doc(this.collectionRef, auditId);
    const current = await getDoc(ref);
    if (!current.exists()) throw new Error('Audit not found');

    const data = current.data();
    if (data.status === 'inchis') {
      throw new Error('Cannot remove witnesses from closed audit');
    }

    const witness = data.witnesses?.find((w) => w.id === witnessId);
    if (!witness) throw new Error('Witness not found');

    await updateDoc(ref, {
      witnesses: arrayRemove(witness),
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
  }

  /**
   * Add action to audit
   * @param {string} auditId
   * @param {object} action - { id, description, type, capaId, assignedTo, assignedToName, dueDate, status }
   * @param {string} userId
   */
  async addAction(auditId, action, userId) {
    if (!auditId) throw new Error('auditId is required');
    if (!action.description) throw new Error('action.description is required');
    if (!action.type || !['masura', 'corectie', 'recomandare', 'capa'].includes(action.type)) {
      throw new Error('action.type must be masura, corectie, recomandare, or capa');
    }

    const ref = doc(this.collectionRef, auditId);
    const current = await getDoc(ref);
    if (!current.exists()) throw new Error('Audit not found');

    const data = current.data();
    if (!['constatari', 'redeschis'].includes(data.status)) {
      throw new Error('Actions can only be added in constatari or redeschis status');
    }

    const newAction = {
      id: action.id || `action_${Date.now()}`,
      description: action.description.trim(),
      type: action.type,
      capaId: action.capaId || null,
      assignedTo: action.assignedTo || null,
      assignedToName: action.assignedToName || null,
      dueDate: action.dueDate ? (action.dueDate instanceof Timestamp ? action.dueDate : Timestamp.fromDate(new Date(action.dueDate))) : null,
      status: action.status || 'open'
    };

    await updateDoc(ref, {
      actions: arrayUnion(newAction),
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
  }

  /**
   * Update action in audit
   * @param {string} auditId
   * @param {string} actionId
   * @param {object} updates
   * @param {string} userId
   */
  async updateAction(auditId, actionId, updates, userId) {
    if (!auditId) throw new Error('auditId is required');
    if (!actionId) throw new Error('actionId is required');

    const ref = doc(this.collectionRef, auditId);
    const current = await getDoc(ref);
    if (!current.exists()) throw new Error('Audit not found');

    const data = current.data();
    const actions = data.actions || [];
    const index = actions.findIndex((a) => a.id === actionId);
    
    if (index === -1) throw new Error('Action not found');

    // Update the action
    actions[index] = {
      ...actions[index],
      ...updates
    };

    if (updates.dueDate && !(updates.dueDate instanceof Timestamp)) {
      actions[index].dueDate = Timestamp.fromDate(new Date(updates.dueDate));
    }

    await updateDoc(ref, {
      actions: actions,
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
  }

  /**
   * Remove action from audit
   * @param {string} auditId
   * @param {string} actionId
   * @param {string} userId
   */
  async removeAction(auditId, actionId, userId) {
    if (!auditId) throw new Error('auditId is required');
    if (!actionId) throw new Error('actionId is required');

    const ref = doc(this.collectionRef, auditId);
    const current = await getDoc(ref);
    if (!current.exists()) throw new Error('Audit not found');

    const data = current.data();
    const action = data.actions?.find((a) => a.id === actionId);
    if (!action) throw new Error('Action not found');

    await updateDoc(ref, {
      actions: arrayRemove(action),
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
  }
}

export const auditServiceQMS = new AuditServiceQMS();
